import { useCallback, useEffect, useState } from "react";
import {
  deleteAdsGoogleImportSecret,
  deleteAdsMetaImportSecret,
  getAdsGoogleImportSecret,
  getAdsMetaImportSecret,
  putAdsGoogleImportSecret,
  putAdsMetaImportSecret,
} from "@/lib/adsAssistantClient";

export function useAdsAssistantFeedImportVaults(scopedOrgId: string | null, enabled: boolean) {
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaConfigured, setMetaConfigured] = useState(false);
  const [metaUpdatedAt, setMetaUpdatedAt] = useState<string | null>(null);
  const [encryptionConfigured, setEncryptionConfigured] = useState(false);
  const [canEditMetaToken, setCanEditMetaToken] = useState(false);
  const [metaBanner, setMetaBanner] = useState<string | null>(null);
  const [saveTokenDraft, setSaveTokenDraft] = useState("");

  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(false);
  const [googleUpdatedAt, setGoogleUpdatedAt] = useState<string | null>(null);
  const [googleEncryptionConfigured, setGoogleEncryptionConfigured] = useState(false);
  const [canEditGoogleCredentials, setCanEditGoogleCredentials] = useState(false);
  const [googleBanner, setGoogleBanner] = useState<string | null>(null);
  const [saveCredentialsDraft, setSaveCredentialsDraft] = useState("");

  const [saveVaultBusy, setSaveVaultBusy] = useState(false);
  const [vaultError, setVaultError] = useState<string | null>(null);

  const loadMetaSecret = useCallback(async () => {
    if (!scopedOrgId) return;
    setMetaLoading(true);
    const r = await getAdsMetaImportSecret();
    setMetaLoading(false);
    if (!r.ok) {
      setMetaConfigured(false);
      setMetaUpdatedAt(null);
      setEncryptionConfigured(false);
      setCanEditMetaToken(false);
      setVaultError(r.error);
      return;
    }
    setVaultError(null);
    setMetaConfigured(!!r.data.configured);
    setMetaUpdatedAt(r.data.updated_at ?? null);
    setEncryptionConfigured(!!r.data.encryption_configured);
    setCanEditMetaToken(!!r.data.can_edit_meta_token);
  }, [scopedOrgId]);

  const loadGoogleSecret = useCallback(async () => {
    if (!scopedOrgId) return;
    setGoogleLoading(true);
    const r = await getAdsGoogleImportSecret();
    setGoogleLoading(false);
    if (!r.ok) {
      setGoogleConfigured(false);
      setGoogleUpdatedAt(null);
      setGoogleEncryptionConfigured(false);
      setCanEditGoogleCredentials(false);
      return;
    }
    setGoogleConfigured(!!r.data.configured);
    setGoogleUpdatedAt(r.data.updated_at ?? null);
    setGoogleEncryptionConfigured(!!r.data.encryption_configured);
    setCanEditGoogleCredentials(!!r.data.can_edit_google_credentials);
  }, [scopedOrgId]);

  useEffect(() => {
    if (!enabled) return;
    void loadMetaSecret();
    void loadGoogleSecret();
  }, [enabled, loadMetaSecret, loadGoogleSecret]);

  useEffect(() => {
    if (!metaBanner) return;
    const t = window.setTimeout(() => setMetaBanner(null), 2800);
    return () => window.clearTimeout(t);
  }, [metaBanner]);

  useEffect(() => {
    if (!googleBanner) return;
    const t = window.setTimeout(() => setGoogleBanner(null), 2800);
    return () => window.clearTimeout(t);
  }, [googleBanner]);

  const saveOrgToken = async () => {
    const raw = saveTokenDraft.trim();
    if (!raw) {
      setMetaBanner(null);
      setVaultError("Paste a page access token before saving.");
      return;
    }
    setSaveVaultBusy(true);
    setVaultError(null);
    const r = await putAdsMetaImportSecret({ page_access_token: raw });
    setSaveVaultBusy(false);
    if (!r.ok) {
      setVaultError(r.error);
      return;
    }
    setSaveTokenDraft("");
    setMetaBanner("Saved org Meta token for feed import.");
    await loadMetaSecret();
  };

  const clearOrgToken = async () => {
    if (
      !window.confirm(
        "Remove the saved Meta page token for this organization? Sales imports will fail until a new token is saved.",
      )
    ) {
      return;
    }
    setSaveVaultBusy(true);
    setVaultError(null);
    const r = await deleteAdsMetaImportSecret();
    setSaveVaultBusy(false);
    if (!r.ok) {
      setVaultError(r.error);
      return;
    }
    setMetaBanner("Cleared saved Meta token.");
    await loadMetaSecret();
  };

  const saveOrgGoogleCredentials = async () => {
    const raw = saveCredentialsDraft.trim();
    if (!raw) {
      setGoogleBanner(null);
      setVaultError("Paste service account JSON before saving.");
      return;
    }
    setSaveVaultBusy(true);
    setVaultError(null);
    const r = await putAdsGoogleImportSecret({ service_account_json: raw });
    setSaveVaultBusy(false);
    if (!r.ok) {
      setVaultError(r.error);
      return;
    }
    setSaveCredentialsDraft("");
    setGoogleBanner("Saved Google credentials for feed import.");
    await loadGoogleSecret();
  };

  const clearOrgGoogleCredentials = async () => {
    if (
      !window.confirm(
        "Remove saved Google service account credentials for this organization? Direct GBP fetch will fail until new credentials are saved.",
      )
    ) {
      return;
    }
    setSaveVaultBusy(true);
    setVaultError(null);
    const r = await deleteAdsGoogleImportSecret();
    setSaveVaultBusy(false);
    if (!r.ok) {
      setVaultError(r.error);
      return;
    }
    setGoogleBanner("Cleared saved Google credentials.");
    await loadGoogleSecret();
  };

  return {
    metaLoading,
    metaConfigured,
    metaUpdatedAt,
    encryptionConfigured,
    canEditMetaToken,
    metaBanner,
    saveTokenDraft,
    setSaveTokenDraft,
    googleLoading,
    googleConfigured,
    googleUpdatedAt,
    googleEncryptionConfigured,
    canEditGoogleCredentials,
    googleBanner,
    saveCredentialsDraft,
    setSaveCredentialsDraft,
    saveVaultBusy,
    vaultError,
    saveOrgToken,
    clearOrgToken,
    saveOrgGoogleCredentials,
    clearOrgGoogleCredentials,
  };
}
